(function () {
  'use strict';
  const api = window.CityOpticsContactLensConverter;
  if (!api) return;

  const MODE = Object.freeze({ GLASSES: 'glasses', CONTACT_LENS: 'contact-lens' });
  const MAX_SPHERE_DIFFERENCE_D = 0.25;
  const MAX_CYLINDER_DIFFERENCE_D = 0.25;
  const MAX_AXIS_DIFFERENCE_DEG = 5;
  const MAX_NEAREST_AXIS_DISTANCE_DEG = MAX_AXIS_DIFFERENCE_DEG;
  const CONTEXT = Object.freeze({
    GENERIC: 'generic',
    COLOUR_COLLECTION: 'colour_collection',
    CONTACT_COLLECTION: 'contact_collection',
    PDP: 'pdp'
  });
  const pushEvent = (name, details = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...details });
  };
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  const safeStorePath = (value) => {
    try { const url = new URL(value, window.location.href); return url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : '#'; } catch (_) { return '#'; }
  };
  const safeImage = (value) => {
    try { const url = new URL(value, window.location.origin); return ['https:', 'http:'].includes(url.protocol) ? url.href : ''; } catch (_) { return ''; }
  };
  const POWER_INPUT = Object.freeze({
    sphere: Object.freeze({ min: api.CONFIG?.SPHERE_MIN_D ?? -30, max: api.CONFIG?.SPHERE_MAX_D ?? 30, stepError: 'Enter power in 0.25 D steps.' }),
    cylinder: Object.freeze({ min: api.CONFIG?.CYLINDER_MIN_D ?? -10, max: api.CONFIG?.CYLINDER_MAX_D ?? 10, stepError: 'Enter cylinder in 0.25 D steps.' })
  });
  const SPHERE_SUGGESTION_RANGE = Object.freeze({ min: -15, max: 15 });
  const powerInputConfig = (kind) => POWER_INPUT[kind] || POWER_INPUT.sphere;
  const cleanPowerText = (value) => String(value ?? '').trim().replace(/\u2212/g, '-');
  const formatPowerInput = (value) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';
    if (Math.abs(numeric) < 0.000001) return '0.00';
    return `${numeric > 0 ? '+' : ''}${numeric.toFixed(2)}`;
  };
  const parsePowerInput = (value, kind = 'sphere') => {
    const config = powerInputConfig(kind);
    const text = cleanPowerText(value);
    if (!text) return { empty: true, valid: true, value: null, formatted: '' };
    if (kind === 'sphere' && /^(?:pl|plano)$/i.test(text)) return { empty: false, valid: true, value: 0, formatted: '0.00' };
    if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/.test(text)) return { empty: false, valid: false, error: `Enter a value between ${formatPowerInput(config.min)} and ${formatPowerInput(config.max)}.` };
    const numeric = Number(text);
    if (!Number.isFinite(numeric) || numeric < config.min || numeric > config.max) return { empty: false, valid: false, error: `Enter a value between ${formatPowerInput(config.min)} and ${formatPowerInput(config.max)}.` };
    if (!api.isQuarterStep(numeric)) return { empty: false, valid: false, error: config.stepError };
    return { empty: false, valid: true, value: numeric, formatted: formatPowerInput(numeric) };
  };
  const powerOptions = (kind = 'sphere') => {
    const config = powerInputConfig(kind);
    const values = [];
    if (kind === 'cylinder') {
      for (let candidate = -0.25; candidate >= config.min - 0.001; candidate -= 0.25) values.push(Number(candidate.toFixed(2)));
    } else {
      const suggestionMin = Math.max(config.min, SPHERE_SUGGESTION_RANGE.min);
      const suggestionMax = Math.min(config.max, SPHERE_SUGGESTION_RANGE.max);
      for (let candidate = suggestionMin; candidate <= suggestionMax + 0.001; candidate += 0.25) values.push(Number(candidate.toFixed(2)));
    }
    return values;
  };
  const powerSuggestions = (value, kind = 'sphere', limit = 6) => {
    const config = powerInputConfig(kind);
    const text = cleanPowerText(value);
    const plano = kind === 'sphere' && /^(?:p|pl|pla|plan|plano)$/i.test(text);
    const numeric = plano ? 0 : Number(text);
    const target = Number.isFinite(numeric) ? Math.min(config.max, Math.max(config.min, numeric)) : 0;
    const visibleLimit = Math.max(1, Math.min(8, Number(limit) || 6));
    const values = powerOptions(kind);
    if (!text) return values;
    const nearby = [...values]
      .sort((left, right) => Math.abs(left - target) - Math.abs(right - target))
      .slice(0, visibleLimit);
    return kind === 'cylinder'
      ? nearby.sort((left, right) => Math.abs(left) - Math.abs(right))
      : nearby.sort((left, right) => left - right);
  };
  const axisInputValid = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return false;
    const numeric = Number(text);
    return Number.isInteger(numeric) && numeric >= 1 && numeric <= 180;
  };
  const normaliseProductType = (value) => ['spherical', 'toric', 'multifocal', 'other'].includes(value) ? value : 'other';
  const safeReturn = (params, returnProducts = []) => {
    const handle = params.get('return_product') || '';
    if (!/^[a-z0-9][a-z0-9-]*$/.test(handle)) return null;
    const structuredProduct = Array.isArray(returnProducts)
      ? returnProducts.find((item) => item && item.handle === handle)
      : null;
    if (Array.isArray(returnProducts) && returnProducts.length > 0 && !structuredProduct) return null;
    const url = new URL(`/products/${handle}`, window.location.origin);
    const variant = params.get('variant') || '';
    const colour = params.get('colour') || '';
    const shadeMedia = params.get('shade_media') || params.get('shade-media') || params.get('shade') || '';
    if (/^\d{1,24}$/.test(variant)) url.searchParams.set('variant', variant);
    if (/^[\w .,+&'/-]{1,60}$/.test(colour)) url.searchParams.set('colour', colour);
    if (/^\d{1,24}$/.test(shadeMedia)) url.searchParams.set('shade_media', shadeMedia);
    const shadeLabel = params.get('colour') || params.get('shade_name') || '';
    return {
      handle,
      href: `${url.pathname}${url.search}`,
      label: (params.get('return_name') || '').slice(0, 100),
      shadeLabel: /^[\w .,+&'/-]{1,60}$/.test(shadeLabel) ? shadeLabel : '',
      productType: normaliseProductType(structuredProduct?.type),
      productCategory: structuredProduct?.category === 'colour' ? 'colour' : 'clear',
      productTypeSource: structuredProduct?.source || 'unknown'
    };
  };

  const resolveContext = (params, returnTarget) => {
    const source = params.get('source');
    if (source === 'colour-contacts') return CONTEXT.COLOUR_COLLECTION;
    if (source === 'contact-lenses') return CONTEXT.CONTACT_COLLECTION;
    if (source === 'pdp' && returnTarget) return CONTEXT.PDP;
    return CONTEXT.GENERIC;
  };

  const highestBand = (eyeResults) => {
    if (eyeResults.some((item) => item.band === 'high')) return 'high';
    if (eyeResults.some((item) => item.band === 'moderate')) return 'moderate';
    if (eyeResults.some((item) => item.band === 'mild')) return 'mild';
    return 'low';
  };

  const resultPriorityModel = (inputMode, band) => {
    if (inputMode === MODE.CONTACT_LENS) return { primary: 'current_toric', secondary: 'spherical' };
    if (inputMode === MODE.GLASSES && band !== 'low') return { primary: 'toric', secondary: 'spherical' };
    return { primary: 'spherical', secondary: 'toric' };
  };
  const allowIndividualSphericalCards = (band) => band === 'low';

  const defaultIntent = (context, returnTarget) => {
    if (context === CONTEXT.COLOUR_COLLECTION || returnTarget?.productCategory === 'colour') return 'colour';
    if (context === CONTEXT.CONTACT_COLLECTION || (context === CONTEXT.PDP && returnTarget)) return 'clear';
    return '';
  };

  const CURRENT_CL_GOAL = Object.freeze({ COLOURED_SPHERICAL: 'coloured-spherical' });
  const currentClDecisionModel = (band, goal = '') => {
    const allowSpherical = band !== 'high';
    const colouredGoal = goal === CURRENT_CL_GOAL.COLOURED_SPHERICAL;
    const autoColouredSpherical = colouredGoal && (band === 'low' || band === 'mild');
    return {
      allowSpherical,
      preferred: band === 'low' ? 'use_spherical' : 'keep_toric',
      sphericalLabel: colouredGoal && band === 'moderate' ? 'Show coloured spherical options anyway' : band === 'moderate' ? 'Use the spherical equivalent anyway' : 'Use the spherical equivalent',
      autoPathway: colouredGoal ? (autoColouredSpherical ? 'use_spherical' : band === 'high' ? 'goal_high' : '') : '',
      autoSphericalIntent: autoColouredSpherical ? 'colour' : ''
    };
  };
  const currentClSphericalCopy = (band, coloured = false) => ({
    low: 'A spherical contact lens may provide acceptable vision for many wearers at this level of astigmatism.',
    mild: `A spherical${coloured ? ' coloured' : ''} lens can sometimes be worn at this level, but vision may be mildly blurred because the cylinder correction is omitted.`,
    moderate: 'A spherical lens may leave noticeable or moderate blur because the cylinder correction is omitted.',
    high: 'A spherical contact lens is generally not recommended at this level of astigmatism because a substantial amount of cylinder correction would be omitted.'
  }[band]);

  const cylinderWarning = (band, cylinder) => ({
    low: `The ${Math.abs(cylinder).toFixed(2)} D cylinder correction is omitted. A spherical lens may provide acceptable vision for some wearers at this level of astigmatism.`,
    mild: `Vision may be mildly blurred because the ${Math.abs(cylinder).toFixed(2)} D cylinder correction is omitted.`,
    moderate: `Vision may be noticeably or moderately blurred because the ${Math.abs(cylinder).toFixed(2)} D cylinder correction is omitted.`,
    high: `A substantial ${Math.abs(cylinder).toFixed(2)} D cylinder correction would be omitted. The toric shopping pathway is shown first.`
  }[band]);

  const selectIntentCatalogue = (intent, catalogues) => intent === 'clear'
    ? catalogues.clear
    : intent === 'colour'
      ? catalogues.colour
      : [];

  const productMatchesIntent = (product, intent) => intent === 'clear'
    ? product?.category === 'clear'
    : intent === 'colour'
      ? product?.category === 'colour'
      : false;
  const selectToricIntentCatalogue = (intent, catalogue) => (Array.isArray(catalogue) ? catalogue : [])
    .filter((product) => product?.type === 'toric' && productMatchesIntent(product, intent));
  const colouredAstigmatismShoppingCopy = (band) => ({
    mild: 'Most coloured lenses in our current range are spherical, so some cylinder correction would be omitted. Vision may be mildly blurred.',
    moderate: 'Most coloured lenses in our current range are spherical, so some cylinder correction would be omitted. Vision may be noticeably or moderately blurred.',
    high: 'Your astigmatism is high enough that a spherical coloured contact lens is generally not recommended because a substantial amount of cylinder correction would be omitted.'
  }[band] || 'Most coloured lenses in our current range are spherical, so some cylinder correction would be omitted.');
  const intentRecommendationModel = (intent, band, eligibleToricCount = 0, toricMatchCount = 0, sphericalMatchCount = 0) => {
    if (intent === 'checking') return { pathway: 'support_only', productCards: false, categoryShopping: false };
    if (band !== 'low' && intent === 'clear') return { pathway: 'clear_toric', productCards: toricMatchCount > 0, categoryShopping: true };
    if (band !== 'low' && intent === 'colour') return { pathway: eligibleToricCount > 0 ? 'colour_toric' : 'colour_toric_guidance', productCards: toricMatchCount > 0, categoryShopping: true };
    if (intent === 'clear' || intent === 'colour') return { pathway: `${intent}_spherical`, productCards: sphericalMatchCount > 0, categoryShopping: true };
    return { pathway: 'choose_intent', productCards: false, categoryShopping: false };
  };

  const exactMatchingProducts = (catalogue, eyeResults) => catalogue.filter((product) => eyeResults.every((eye) => {
    const match = api.productPowerMatch(product, eye.sphericalDisplay);
    return match.matched && match.type === api.POWER_MATCH.EXACT;
  }));
  const closestListedSphereOptions = (product, target, maximumDifference = MAX_SPHERE_DIFFERENCE_D) => {
    const available = numericValues(product?.sphereValues);
    if (!available.length || !Number.isFinite(Number(target))) return [];
    const ranked = available
      .map((power) => ({ power, difference: Math.abs(power - Number(target)) }))
      .filter((option) => option.difference <= maximumDifference + 0.001)
      .sort((left, right) => left.difference - right.difference || left.power - right.power);
    if (!ranked.length) return [];
    const nearestDifference = ranked[0].difference;
    return ranked.filter((option) => Math.abs(option.difference - nearestDifference) < 0.001);
  };
  const closestSphericalProductMatches = (catalogue, eyeResults, maximumDifference = MAX_SPHERE_DIFFERENCE_D) => (Array.isArray(catalogue) ? catalogue : [])
    .map((product) => {
      const eyeMatches = (Array.isArray(eyeResults) ? eyeResults : []).filter((eye) => eye?.valid).map((eye) => ({
        eye: eye._eye || 'od',
        label: eye._eye === 'os' ? 'Left eye / OS' : 'Right eye / OD',
        target: Number(eye.sphericalDisplay),
        options: closestListedSphereOptions(product, Number(eye.sphericalDisplay), maximumDifference)
      }));
      if (!eyeMatches.length || eyeMatches.some((eye) => !eye.options.length)) return null;
      const differences = eyeMatches.map((eye) => eye.options[0].difference);
      if (differences.every((difference) => difference < 0.001)) return null;
      return {
        product,
        eyeMatches,
        maximumDifference: Math.max(...differences),
        totalDifference: differences.reduce((sum, difference) => sum + difference, 0)
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.maximumDifference - right.maximumDifference || left.totalDifference - right.totalDifference || left.product.title.localeCompare(right.product.title));
  const prioritiseReturnProduct = (matches, returnHandle = '', limit = 4) => {
    const products = Array.isArray(matches) ? matches : [];
    const returnProduct = products.find((product) => product?.handle === returnHandle);
    const ordered = returnProduct ? [returnProduct, ...products.filter((product) => product !== returnProduct)] : products;
    return limit > 0 ? ordered.slice(0, limit) : ordered;
  };

  const numericValues = (values) => [...new Set((Array.isArray(values) ? values : [])
    .map((value) => Number.parseFloat(value))
    .filter(Number.isFinite))];
  const samePower = (left, right) => Math.abs(Number(left) - Number(right)) < 0.001;
  const hasExactValue = (values, target) => numericValues(values).some((value) => samePower(value, target));
  const normaliseAxis = (axis) => {
    const value = Number(axis);
    if (!Number.isFinite(value)) return null;
    const normalised = ((value - 1) % 180 + 180) % 180 + 1;
    return normalised === 0 ? 180 : normalised;
  };
  const axisDistance = (targetAxis, availableAxis) => {
    const target = normaliseAxis(targetAxis);
    const available = normaliseAxis(availableAxis);
    if (target === null || available === null) return Number.POSITIVE_INFINITY;
    const difference = Math.abs(target - available);
    return Math.min(difference, 180 - difference);
  };
  const ruleCylinderAxes = (product, sphere, cylinder) => {
    if (!Array.isArray(product.rules)) return [];
    const productAxes = numericValues(product.axisValues).map(normaliseAxis).filter(Number.isFinite);
    const rules = product.rules.filter((entry) => {
      const minimum = Number(entry?.sphere_min);
      const maximum = Number(entry?.sphere_max);
      return Number.isFinite(minimum) && Number.isFinite(maximum) && sphere >= minimum - 0.001 && sphere <= maximum + 0.001;
    });
    const axes = rules.flatMap((rule) => {
      if (!rule?.cylinders || typeof rule.cylinders !== 'object') return [];
      const cylinderEntry = Object.entries(rule.cylinders).find(([key]) => samePower(key, cylinder));
      if (!cylinderEntry) return [];
      return cylinderEntry[1] === 'all' ? productAxes : numericValues(cylinderEntry[1]).map(normaliseAxis).filter(Number.isFinite);
    });
    return [...new Set(axes)].filter((axis) => !productAxes.length || productAxes.some((listed) => samePower(listed, axis)));
  };
  const toricTargetForResult = (inputMode, result) => {
    if (!result?.valid) return null;
    const source = inputMode === MODE.CONTACT_LENS ? result.normalized : result.toric;
    if (!source || Math.abs(Number(source.cylinderDisplay ?? source.cylinder)) < 0.75) return null;
    const sphere = Number(source.sphereDisplay ?? source.sphere);
    const cylinder = Number(source.cylinderDisplay ?? source.cylinder);
    const axis = normaliseAxis(source.axis);
    if (![sphere, cylinder, axis].every(Number.isFinite)) return null;
    return { sphere, cylinder, axis };
  };
  const compareToricCandidates = (left, right) => left.cylinderDifference - right.cylinderDifference
    || left.sphereDifference - right.sphereDifference
    || left.axisDifference - right.axisDifference
    || left.sphere - right.sphere
    || left.cylinder - right.cylinder
    || left.axis - right.axis;
  const sameToricScore = (left, right) => samePower(left.cylinderDifference, right.cylinderDifference)
    && samePower(left.sphereDifference, right.sphereDifference)
    && samePower(left.axisDifference, right.axisDifference);
  const listedToricCandidates = (product, target) => {
    if (!product || !target || !Array.isArray(product.rules)) return [];
    const spheres = numericValues(product.sphereValues).filter((sphere) => Math.abs(sphere - target.sphere) <= MAX_SPHERE_DIFFERENCE_D + 0.001);
    const cylinders = numericValues(product.cylinderValues).filter((cylinder) => Math.abs(cylinder - target.cylinder) <= MAX_CYLINDER_DIFFERENCE_D + 0.001);
    const candidates = [];
    spheres.forEach((sphere) => cylinders.forEach((cylinder) => {
      ruleCylinderAxes(product, sphere, cylinder).forEach((axis) => {
        const candidate = {
          sphere,
          cylinder,
          axis,
          sphereDifference: Math.abs(sphere - target.sphere),
          cylinderDifference: Math.abs(cylinder - target.cylinder),
          axisDifference: axisDistance(target.axis, axis)
        };
        if (candidate.axisDifference <= MAX_AXIS_DIFFERENCE_DEG + 0.001) candidates.push(candidate);
      });
    }));
    const unique = [...new Map(candidates.map((candidate) => [`${candidate.sphere}|${candidate.cylinder}|${candidate.axis}`, candidate])).values()];
    return unique.sort(compareToricCandidates);
  };
  const matchToricProduct = (product, target) => {
    const candidates = listedToricCandidates(product, target);
    if (!candidates.length) return null;
    const exact = candidates.filter((candidate) => samePower(candidate.sphereDifference, 0) && samePower(candidate.cylinderDifference, 0) && samePower(candidate.axisDifference, 0));
    const options = exact.length ? exact : candidates.filter((candidate) => sameToricScore(candidate, candidates[0]));
    return {
      state: exact.length ? 'exact' : 'closest',
      options,
      axes: options.map((candidate) => ({ axis: candidate.axis, distance: candidate.axisDifference })),
      target
    };
  };
  const hasExactToricPowerWithoutCloseAxis = (catalogue, targets) => (Array.isArray(catalogue) ? catalogue : []).some((product) => {
    const powerAxes = targets.map(({ target }) => {
      if (!target || !hasExactValue(product.sphereValues, target.sphere) || !hasExactValue(product.cylinderValues, target.cylinder)) return null;
      const axes = ruleCylinderAxes(product, target.sphere, target.cylinder);
      return axes.length ? { target, axes } : null;
    });
    return powerAxes.every(Boolean) && powerAxes.some(({ target, axes }) => axes.every((axis) => axisDistance(target.axis, axis) > MAX_NEAREST_AXIS_DISTANCE_DEG));
  });
  const toricProductScore = (entry) => {
    const options = entry.eyeMatches.map(({ match }) => match.options[0]);
    const exactRank = entry.eyeMatches.every(({ match }) => match.state === 'exact') ? 0 : 1;
    const maximum = (key) => Math.max(...options.map((option) => option[key]));
    const total = (key) => options.reduce((sum, option) => sum + option[key], 0);
    return [exactRank, maximum('cylinderDifference'), total('cylinderDifference'), maximum('sphereDifference'), total('sphereDifference'), maximum('axisDifference'), total('axisDifference')];
  };
  const compareScoreTuples = (left, right) => {
    for (let index = 0; index < Math.max(left.length, right.length); index += 1) {
      if (!samePower(left[index] || 0, right[index] || 0)) return (left[index] || 0) - (right[index] || 0);
    }
    return 0;
  };
  const matchToricProducts = (catalogue, targets) => (Array.isArray(catalogue) ? catalogue : [])
    .map((product, catalogueIndex) => ({ product, catalogueIndex, eyeMatches: targets.map((target) => ({ ...target, match: matchToricProduct(product, target.target) })) }))
    .filter((entry) => entry.eyeMatches.length > 0 && entry.eyeMatches.every((eye) => eye.match))
    .map((entry) => ({ ...entry, score: toricProductScore(entry) }))
    .sort((left, right) => compareScoreTuples(left.score, right.score) || left.catalogueIndex - right.catalogueIndex);

  const discoveryOrderModel = (band, returnTarget) => {
    const toricPrimary = band !== 'low';
    if (!toricPrimary) return returnTarget ? ['return', 'spherical', 'toric'] : ['spherical', 'toric'];
    if (returnTarget?.productType === 'toric') return ['return', 'toric', 'spherical'];
    return returnTarget ? ['toric', 'return', 'spherical'] : ['toric', 'spherical'];
  };

  const routeModel = (context, band, hasMatches, returnTarget) => {
    const toric = { label: 'View toric contact lenses', href: '/collections/toric-astigmatism-contact-lenses', route: 'toric' };
    const spherical = { label: 'Shop spherical contact lenses', href: '/collections/spherical-contact-lenses', route: 'spherical' };
    const support = { label: 'Ask our optometry team', href: '/pages/contact#ContactForm', route: 'support' };

    if (context === CONTEXT.PDP && returnTarget) {
      const returnAction = {
        label: returnTarget.label ? `Return to ${returnTarget.label}` : 'Return to product',
        href: returnTarget.href,
        route: 'return'
      };
      const toricOrigin = returnTarget.productType === 'toric';
      if (toricOrigin && (band === 'moderate' || band === 'high')) {
        return {
          eyebrow: 'Continue with your toric product',
          actions: [returnAction, support],
          showMatches: false,
          returnShade: returnTarget.shadeLabel,
          analyticsRoute: 'toric_product_return_primary'
        };
      }
      if (band === 'high') {
        return {
          eyebrow: 'Toric pathway preferred',
          actions: [toric, support, { ...returnAction, label: `${returnAction.label} anyway`, tertiary: true }],
          showMatches: false,
          returnShade: returnTarget.shadeLabel,
          analyticsRoute: 'toric_primary'
        };
      }
      if (band === 'moderate') {
        return {
          eyebrow: 'Toric pathway preferred',
          actions: [toric, { ...returnAction, label: `${returnAction.label} anyway` }, support],
          showMatches: false,
          returnShade: returnTarget.shadeLabel,
          analyticsRoute: 'toric_primary'
        };
      }
      return {
        eyebrow: 'Return to your product',
        actions: [returnAction, toric, support],
        showMatches: false,
        returnShade: returnTarget.shadeLabel,
        analyticsRoute: 'return_primary'
      };
    }

    if (context === CONTEXT.COLOUR_COLLECTION) {
      if (band === 'high') return { eyebrow: 'Next step', actions: [toric, support], showMatches: false };
      const colourAction = {
        label: hasMatches ? 'View coloured lenses in this power' : 'Shop coloured contact lenses',
        href: hasMatches ? '#ClcProductMatchesList' : '/collections/colour-contacts',
        route: 'colour'
      };
      if (band === 'moderate') {
        return {
          eyebrow: 'Next step',
          actions: [toric, ...(hasMatches ? [{ ...colourAction, label: 'View spherical coloured lenses anyway' }] : []), support],
          showMatches: hasMatches,
          matchesSecondary: true
        };
      }
      return { eyebrow: 'Next step', actions: [colourAction, toric, support], showMatches: hasMatches };
    }

    if (band === 'high') return { eyebrow: 'Next step', actions: [toric, support], showMatches: false };
    if (band === 'moderate') {
      return {
        eyebrow: 'Next step',
        actions: [toric, { ...spherical, label: 'View spherical contact lenses anyway' }, support],
        showMatches: false
      };
    }
    return { eyebrow: 'Next step', actions: [spherical, toric, support], showMatches: false };
  };

  const init = (root) => {
    if (root.dataset.initialised === 'true') return;
    root.dataset.initialised = 'true';
    const form = root.querySelector('[data-clc-form]');
    const results = root.querySelector('[data-clc-results]');
    const errors = root.querySelector('[data-clc-errors]');
    const productDataNode = root.querySelector('[data-clc-products]');
    const contactSphereProductDataNode = root.querySelector('[data-clc-contact-sphere-products]');
    const clearProductDataNode = root.querySelector('[data-clc-clear-products]');
    const returnProductDataNode = root.querySelector('[data-clc-return-products]');
    const toricProductDataNode = root.querySelector('[data-clc-toric-products]');
    const colourProducts = productDataNode ? JSON.parse(productDataNode.textContent || '[]') : [];
    const contactSphereProducts = contactSphereProductDataNode ? JSON.parse(contactSphereProductDataNode.textContent || '[]') : [];
    const clearProducts = clearProductDataNode ? JSON.parse(clearProductDataNode.textContent || '[]') : [];
    const returnProducts = returnProductDataNode ? JSON.parse(returnProductDataNode.textContent || '[]') : [];
    const toricProducts = toricProductDataNode ? JSON.parse(toricProductDataNode.textContent || '[]') : [];
    const params = new URLSearchParams(window.location.search);
    const returnTarget = safeReturn(params, returnProducts);
    const context = resolveContext(params, returnTarget);
    const analyticsEntrySource = context === CONTEXT.PDP
      ? (returnTarget?.productType === 'toric' ? 'toric_pdp' : returnTarget?.productCategory === 'colour' ? 'coloured_spherical_pdp' : 'clear_spherical_pdp')
      : context;
    const analyticsPdpType = context === CONTEXT.PDP
      ? (returnTarget?.productType === 'toric' ? 'toric' : returnTarget?.productCategory === 'colour' ? 'coloured_spherical' : 'clear_spherical')
      : '';
    const analyticsDetails = () => ({
      converter_entry_source: analyticsEntrySource,
      converter_mode: mode === MODE.CONTACT_LENS ? 'contact_lens' : 'glasses',
      converter_goal: currentClGoal === CURRENT_CL_GOAL.COLOURED_SPHERICAL ? 'coloured_spherical' : 'generic',
      ...(analyticsPdpType ? { converter_pdp_type: analyticsPdpType } : {})
    });
    const modeInputs = [...root.querySelectorAll('input[name="clc_input_mode"]')];
    const modeDescription = root.querySelector('[data-clc-mode-description]');
    const modeNote = root.querySelector('[data-clc-mode-note]');
    const formTitle = root.querySelector('[data-clc-form-title]');
    const formIntro = root.querySelector('[data-clc-form-intro]');
    const bvdAssumption = root.querySelector('[data-clc-bvd-assumption]');
    const submitLabel = root.querySelector('[data-clc-submit-label]');
    const resultsTitle = root.querySelector('[data-clc-results-title]');
    const intentControl = root.querySelector('[data-clc-intent]');
    const intentLegend = intentControl?.querySelector('legend');
    const intentDescription = intentControl?.querySelector(':scope > p');
    const intentOptions = [...root.querySelectorAll('.clc-intent__options label')].map((label) => ({ label, input: label.querySelector('input'), text: label.querySelector('span') }));
    const intentInputs = intentOptions.map((option) => option.input);
    let mode = params.get('mode') === MODE.CONTACT_LENS ? MODE.CONTACT_LENS : MODE.GLASSES;
    const currentClGoal = params.get('goal') === CURRENT_CL_GOAL.COLOURED_SPHERICAL ? CURRENT_CL_GOAL.COLOURED_SPHERICAL : '';
    let latestEyeResults = [];
    let intent = mode === MODE.CONTACT_LENS ? '' : defaultIntent(context, returnTarget);
    let sphericalIntent = '';
    let converterStarted = false;
    const contactCylinderSelects = [...root.querySelectorAll('[data-clc-contact-cylinder]')];
    const contactSphereSelects = [...root.querySelectorAll('[data-clc-contact-sphere]')];
    const contactAxisSelects = [...root.querySelectorAll('[data-clc-contact-axis]')];
    const toricCylinderValues = [...new Set(toricProducts
      .flatMap((product) => numericValues(product.cylinderValues))
      .filter((value) => Number.isFinite(value) && value < -0.001))]
      .sort((a, b) => b - a);
    const contactSphereValues = [...new Set(contactSphereProducts
      .flatMap((values) => numericValues(values))
      .filter((value) => Number.isFinite(value)))]
      .sort((a, b) => a - b);
    const toricAxisValues = Array.from({ length: 36 }, (_, index) => 180 - (index * 5));
    contactCylinderSelects.forEach((select) => {
      select.innerHTML = `<option value="">Select cylinder</option>${toricCylinderValues.map((value) => `<option value="${value}">${formatPowerInput(value)}</option>`).join('')}`;
    });
    contactSphereSelects.forEach((select) => {
      select.innerHTML = `<option value="">Select sphere</option>${contactSphereValues.map((value) => `<option value="${value}">${formatPowerInput(value)}</option>`).join('')}`;
    });
    contactAxisSelects.forEach((select) => {
      select.innerHTML = `<option value="">Select axis</option>${toricAxisValues.map((value) => `<option value="${value}">${value}°</option>`).join('')}`;
    });

    const configureIntentControl = (band = 'mild') => {
      if (!intentControl || intentOptions.length < 3) return;
      const container = intentControl.querySelector('.clc-intent__options');
      intentOptions.forEach(({ label }) => { label.hidden = false; label.classList.remove('is-primary'); });
      if (mode === MODE.CONTACT_LENS) {
        const decision = currentClDecisionModel(band, currentClGoal);
        intentLegend.textContent = 'What would you like to do?';
        intentDescription.textContent = ({
          low: 'A spherical pathway is shown first, with toric correction still available.',
          mild: 'Toric correction is shown first; you can still explicitly explore the spherical equivalent.',
          moderate: 'Toric correction is likely to provide clearer vision.',
          high: 'Spherical shopping is not shown at this level of astigmatism.'
        }[band] || 'Choose the optical pathway you want to explore next.');
        const definitions = [
          { value: 'keep_toric', label: 'Keep my astigmatism correction' },
          { value: 'use_spherical', label: decision.sphericalLabel },
          { value: 'checking', label: 'Just checking my power' }
        ];
        intentOptions.forEach((option, index) => {
          option.input.value = definitions[index].value;
          option.text.textContent = definitions[index].label;
        });
        intentOptions[1].label.hidden = !decision.allowSpherical;
        const ordered = decision.preferred === 'use_spherical'
          ? [intentOptions[1], intentOptions[0], intentOptions[2]]
          : [intentOptions[0], intentOptions[1], intentOptions[2]];
        ordered.forEach((option) => container.appendChild(option.label));
      } else {
        intentLegend.textContent = 'What would you like to do next?';
        intentDescription.textContent = 'Choose an option to see the safest relevant next step.';
        const definitions = [
          { value: 'clear', label: 'Clear contact lenses' },
          { value: 'colour', label: 'Coloured contact lenses' },
          { value: 'checking', label: 'Just checking my power' }
        ];
        intentOptions.forEach((option, index) => {
          option.input.value = definitions[index].value;
          option.text.textContent = definitions[index].label;
          container.appendChild(option.label);
        });
      }
      intentInputs.forEach((input) => { input.checked = input.value === intent; });
    };

    const setAxisRequirement = (combobox) => {
      if (!combobox) return;
      if (combobox.dataset.powerKind !== 'cylinder' && !combobox.matches('[data-clc-contact-cylinder]')) return;
      const fieldset = combobox.closest('.clc-eye');
      const cylinder = mode === MODE.CONTACT_LENS
        ? fieldset?.querySelector('[data-clc-contact-cylinder]')
        : fieldset?.querySelector('[data-clc-glasses-cylinder] [data-clc-power-input]');
      const axis = mode === MODE.CONTACT_LENS
        ? fieldset?.querySelector('[data-clc-contact-axis]')
        : fieldset?.querySelector('[data-clc-glasses-axis]');
      const helper = fieldset?.querySelector('[id$="-axis-help"]');
      if (!axis || !cylinder) return;
      const parsed = parsePowerInput(cylinder.value, 'cylinder');
      const required = parsed.valid && !parsed.empty && Math.abs(parsed.value) > 0.0001;
      axis.required = required;
      axis.setAttribute('aria-required', String(required));
      if (helper) helper.textContent = required ? 'Required because cylinder is not 0.00' : 'Not required when cylinder is 0.00';
    };

    const createPowerCombobox = (combobox) => {
      const input = combobox.querySelector('[data-clc-power-input]');
      const listbox = combobox.querySelector('[data-clc-options]');
      const field = combobox.closest('.clc-field');
      const error = field?.querySelector('[data-clc-field-error]');
      const kind = combobox.dataset.powerKind === 'cylinder' ? 'cylinder' : 'sphere';
      let options = [];
      let activeIndex = -1;

      const setError = (message = '') => {
        input.setAttribute('aria-invalid', message ? 'true' : 'false');
        field?.classList.toggle('clc-field--invalid', Boolean(message));
        if (error) { error.textContent = message; error.hidden = !message; }
      };
      const close = () => {
        listbox.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      };
      const updateActive = () => {
        const renderedOptions = [...listbox.querySelectorAll('[role="option"]')];
        renderedOptions.forEach((option, index) => {
          const active = index === activeIndex;
          option.classList.toggle('is-active', active);
          option.setAttribute('aria-selected', String(active));
        });
        if (activeIndex >= 0) {
          input.setAttribute('aria-activedescendant', `${input.id}-option-${activeIndex}`);
          renderedOptions[activeIndex]?.scrollIntoView({ block: 'nearest' });
        } else input.removeAttribute('aria-activedescendant');
      };
      const centreOption = (index) => {
        if (index < 0) return;
        window.requestAnimationFrame(() => {
          const option = listbox.querySelectorAll('[role="option"]')[index];
          if (!option) return;
          listbox.scrollTop = Math.max(0, option.offsetTop - ((listbox.clientHeight - option.offsetHeight) / 2));
        });
      };
      const render = ({ full = false } = {}) => {
        const parsed = parsePowerInput(input.value, kind);
        options = full || !cleanPowerText(input.value) ? powerOptions(kind) : powerSuggestions(input.value, kind, 6);
        activeIndex = -1;
        listbox.innerHTML = options.map((value, index) => `<div id="${input.id}-option-${index}" class="clc-combobox__option" role="option" aria-selected="false" data-value="${value}">${formatPowerInput(value)}</div>`).join('');
        listbox.hidden = false;
        input.setAttribute('aria-expanded', 'true');
        if (full && parsed.valid && !parsed.empty) {
          activeIndex = options.findIndex((value) => Math.abs(value - parsed.value) < 0.001);
          updateActive();
          centreOption(activeIndex);
        } else listbox.scrollTop = 0;
      };
      const select = (index) => {
        if (!Number.isInteger(index) || index < 0 || index >= options.length) return;
        input.value = formatPowerInput(options[index]);
        setError();
        close();
        setAxisRequirement(combobox);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      const validate = ({ normalise = true } = {}) => {
        const parsed = parsePowerInput(input.value, kind);
        setError(parsed.valid ? '' : parsed.error);
        if (parsed.valid && !parsed.empty && normalise) input.value = parsed.formatted;
        setAxisRequirement(combobox);
        return parsed;
      };

      input.addEventListener('focus', () => render({ full: true }));
      input.addEventListener('input', () => { setError(); render(); setAxisRequirement(combobox); });
      input.addEventListener('blur', () => { window.setTimeout(() => { validate(); close(); }, 80); });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          if (listbox.hidden) render({ full: true });
          const direction = event.key === 'ArrowDown' ? 1 : -1;
          activeIndex = (activeIndex + direction + options.length) % options.length;
          updateActive();
        } else if (event.key === 'Enter' && !listbox.hidden && activeIndex >= 0) {
          event.preventDefault();
          select(activeIndex);
        } else if (event.key === 'Escape') {
          event.preventDefault();
          close();
        } else if (event.key === 'Tab') close();
      });
      listbox.addEventListener('pointerdown', (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option) return;
        event.preventDefault();
        select([...listbox.querySelectorAll('[role="option"]')].indexOf(option));
        input.focus();
      });
      setAxisRequirement(combobox);
      return { input, kind, close, validate, reset: () => { setError(); close(); setAxisRequirement(combobox); } };
    };
    const powerComboboxes = [...root.querySelectorAll('[data-clc-combobox]')].map(createPowerCombobox);
    contactCylinderSelects.forEach((select) => select.addEventListener('change', () => setAxisRequirement(select)));

    const applyMode = () => {
      modeInputs.forEach((input) => { input.checked = input.value === mode; });
      const contactLensMode = mode === MODE.CONTACT_LENS;
      modeDescription.textContent = contactLensMode
        ? 'Use your current contact lens prescription to compare toric correction with the approximate spherical equivalent. No vertex-distance conversion is applied.'
        : 'Glasses sit in front of the eye, so higher powers may need vertex-distance conversion before estimating contact lens starting powers.';
      modeNote.textContent = '';
      modeNote.hidden = true;
      formTitle.textContent = contactLensMode ? 'Enter your contact lens prescription' : 'Enter your glasses prescription';
      formIntro.textContent = contactLensMode
        ? 'Enter one or both eyes. Copy the sphere, cylinder and axis exactly from your current contact lens prescription.'
        : 'Enter one or both eyes. Use the sphere, cylinder and axis exactly as written on your current glasses prescription.';
      submitLabel.textContent = 'See my contact lens options';
      resultsTitle.textContent = contactLensMode ? 'Contact lens power results' : 'Estimated starting-power results';
      bvdAssumption.hidden = contactLensMode;
      root.querySelectorAll('.clc-eye').forEach((fieldset) => {
        const glassesSphere = fieldset.querySelector('[data-clc-combobox][data-power-kind="sphere"]');
        const glassesSphereInput = glassesSphere?.querySelector('[data-clc-power-input]');
        const contactSphere = fieldset.querySelector('[data-clc-contact-sphere]');
        const sphereLabel = glassesSphere?.closest('.clc-field')?.querySelector('label');
        const sphereHelp = glassesSphere?.closest('.clc-field')?.querySelector('[id$="-sphere-help"]');
        const glassesCylinder = fieldset.querySelector('[data-clc-glasses-cylinder]');
        const glassesCylinderInput = glassesCylinder?.querySelector('[data-clc-power-input]');
        const contactCylinder = fieldset.querySelector('[data-clc-contact-cylinder]');
        const cylinderLabel = glassesCylinder?.closest('.clc-field')?.querySelector('label');
        const cylinderHelp = glassesCylinder?.closest('.clc-field')?.querySelector('[id$="-cylinder-help"]');
        const glassesAxis = fieldset.querySelector('[data-clc-glasses-axis]');
        const contactAxis = fieldset.querySelector('[data-clc-contact-axis]');
        const axisLabel = glassesAxis?.closest('.clc-field')?.querySelector('label');
        if (glassesSphere) glassesSphere.hidden = contactLensMode;
        if (glassesSphereInput) glassesSphereInput.disabled = contactLensMode;
        if (contactSphere) { contactSphere.hidden = !contactLensMode; contactSphere.disabled = !contactLensMode; }
        if (sphereLabel) sphereLabel.htmlFor = contactLensMode ? contactSphere.id : glassesSphereInput.id;
        if (sphereHelp) sphereHelp.textContent = contactLensMode
          ? 'Select a Sphere power available in the current contact lens range'
          : 'Type your power or scroll to select · e.g. −4.00, +2.25, 0 or PL';
        if (glassesCylinder) glassesCylinder.hidden = contactLensMode;
        if (glassesCylinderInput) glassesCylinderInput.disabled = contactLensMode;
        if (contactCylinder) { contactCylinder.hidden = !contactLensMode; contactCylinder.disabled = !contactLensMode; }
        if (cylinderLabel) cylinderLabel.htmlFor = contactLensMode ? contactCylinder.id : glassesCylinderInput.id;
        if (cylinderHelp) cylinderHelp.textContent = contactLensMode
          ? 'Select an available toric contact lens cylinder · blank = 0.00'
          : 'Scroll to select minus cylinder, or type plus/minus cylinder · blank = 0.00';
        if (glassesAxis) { glassesAxis.hidden = contactLensMode; glassesAxis.disabled = contactLensMode; }
        if (contactAxis) { contactAxis.hidden = !contactLensMode; contactAxis.disabled = !contactLensMode; }
        if (axisLabel) axisLabel.htmlFor = contactLensMode ? contactAxis.id : glassesAxis.id;
        setAxisRequirement(contactLensMode ? contactCylinder : glassesCylinder);
      });
      configureIntentControl(latestEyeResults.length ? highestBand(latestEyeResults) : 'mild');
    };
    applyMode();
    const returnWrap = root.querySelector('[data-clc-return]');
    const returnLink = returnWrap?.querySelector('a');
    if (returnTarget && returnWrap && returnLink) {
      returnLink.href = safeStorePath(returnTarget.href);
      returnLink.textContent = `← Back to ${returnTarget.label || 'product'}`;
      returnWrap.hidden = false;
    }
    const markConverterStarted = () => {
      if (converterStarted) return;
      converterStarted = true;
      pushEvent('converter_started', analyticsDetails());
    };
    root.addEventListener('input', markConverterStarted);
    root.addEventListener('change', markConverterStarted);

    const readEye = (eye) => ({
      sphere: mode === MODE.CONTACT_LENS
        ? root.querySelector(`#Clc-${eye}-contact-sphere`).value
        : form.elements[`${eye}_sphere`].value,
      cylinder: mode === MODE.CONTACT_LENS
        ? root.querySelector(`#Clc-${eye}-contact-cylinder`).value
        : form.elements[`${eye}_cylinder`].value,
      axis: mode === MODE.CONTACT_LENS
        ? root.querySelector(`#Clc-${eye}-contact-axis`).value
        : form.elements[`${eye}_axis`].value
    });

    const createResult = (eye, result) => {
      const contactLensMode = mode === MODE.CONTACT_LENS;
      const priority = resultPriorityModel(mode, result.band);
      const meaningfulCylinder = Math.abs(result.normalized.cylinder) >= 0.75;
      const currentRx = api.formatPrescription(result.input);
      const toric = result.toric
        ? `${api.formatPower(result.toric.sphereDisplay, false)} / ${api.formatPower(result.toric.cylinderDisplay, false)}${result.toric.cylinderDisplay === 0 ? '' : ` × ${result.toric.axis}`}`
        : currentRx;
      const source = contactLensMode ? 'Current contact lens prescription' : 'Glasses prescription';
      const primary = priority.primary === 'toric'
        ? `<div class="clc-result__primary clc-result__primary--toric"><span>Theoretical toric starting power</span><strong class="clc-result__path">Toric contact lens</strong><strong class="clc-result__toric-power">${toric}</strong><em>Theoretical starting power only — lens rotation, fit and over-refraction can change the final prescription.</em></div>`
        : priority.primary === 'current_toric'
          ? `<div class="clc-result__primary clc-result__primary--toric"><span>Current toric contact lens prescription</span><strong class="clc-result__toric-power">${currentRx}</strong><em>Shown exactly as entered. No vertex-distance conversion or new toric calculation has been applied.</em></div>`
          : `<div class="clc-result__primary"><span>${contactLensMode ? 'Estimated spherical equivalent' : 'Estimated spherical contact lens starting power'}</span><strong class="clc-result__power">${api.formatPower(result.sphericalDisplay)}</strong><em>${contactLensMode ? 'From your current contact lens prescription' : 'Educational starting estimate'}</em></div>`;
      const secondary = contactLensMode
        ? `<div class="clc-result__alternative clc-result__alternative--${result.band}"><span>Spherical equivalent</span><strong>${api.formatPower(result.sphericalDisplay)}</strong><p>${currentClSphericalCopy(result.band)}</p></div>`
        : meaningfulCylinder
          ? `<div class="clc-result__alternative clc-result__alternative--${result.band}"><span>Spherical alternative</span><strong>${api.formatPower(result.sphericalDisplay)}</strong><p>${cylinderWarning(result.band, result.normalized.cylinder)}</p></div>`
          : `<div class="clc-result__alternative"><span>Toric pathway</span><strong>Usually not required at this cylinder level</strong><p>${Math.abs(result.normalized.cylinder) > 0 ? cylinderWarning(result.band, result.normalized.cylinder) : 'A toric lens may still be discussed if vision is not clear through a spherical lens.'}</p></div>`;
      const details = contactLensMode
        ? `${result.input.cylinder > 0 ? `<p>Normalised to minus cylinder: ${api.formatPrescription(result.normalized)}.</p>` : ''}<p>Spherical equivalent: ${api.formatPower(result.sphericalEquivalent)}, displayed as ${api.formatPower(result.sphericalDisplay)} after quarter-dioptre rounding.</p><p>No vertex-distance conversion is applied because the entered prescription is already at the corneal plane.</p>`
        : `${result.input.cylinder > 0 ? `<p>Normalised to minus cylinder: ${api.formatPrescription(result.normalized)}.</p>` : ''}<p>Spherical equivalent: ${api.formatPower(result.sphericalEquivalent)}. Vertexed at ${api.CONFIG.DEFAULT_BVD_MM} mm: ${api.formatPower(result.sphericalUnrounded)} unrounded, displayed as ${api.formatPower(result.sphericalDisplay)}.</p><p>The theoretical toric starting power is ${toric}.</p>`;
      return `<article class="clc-result clc-result--${result.band}">
        <header class="clc-result__header"><p class="clc-result__eye">${eye === 'od' ? 'Right eye / OD' : 'Left eye / OS'}</p><p class="clc-result__spectacle"><span>${source}</span>${currentRx}</p></header>
        <div class="clc-result__guidance"><span class="clc-band">${result.guidance.label}</span></div>
        ${primary}${secondary}
        <details class="clc-details"><summary>How this estimate is calculated</summary><div>${details}</div></details>
      </article>`;
    };

    const matchingProducts = (catalogue, eyeResults, limit = 4) => {
      const valid = eyeResults.filter((item) => item.valid);
      if (!valid.length) return [];
      const matches = exactMatchingProducts(catalogue, valid);
      return limit > 0 ? matches.slice(0, limit) : matches;
    };

    const productCards = (matches, kind, powerLabel = 'estimated power') => {
      if (!matches.length) return `<div class="clc-pathway"><h3>No exact ${kind} product matches found</h3><p>The estimate is not present in the exact orderable sphere values currently stored for these products. Browse the category or ask our optometry team to confirm availability.</p></div>`;
      return `<section id="ClcProductMatchesList" class="clc-matches"><h3>${kind === 'coloured' ? 'Coloured' : 'Clear spherical'} lenses available in this ${powerLabel}</h3><p>Each product below lists the spherical power. This confirms stored parameter availability only; it does not assess fit or guarantee equivalent vision.</p><div class="clc-matches__grid">${matches.map((product) => `<a href="${escapeHtml(safeStorePath(product.url))}" class="clc-product" data-clc-route="${kind}_product"><img src="${escapeHtml(safeImage(product.image))}" alt="" loading="lazy" width="180" height="180"><span><strong>${escapeHtml(product.title)}</strong><small class="clc-product__availability clc-product__availability--exact">Available in this spherical power</small></span></a>`).join('')}</div></section>`;
    };

    const closestSphericalProductCards = (matches, kind) => {
      const categoryHref = kind === 'coloured' ? '/collections/colour-contacts' : '/collections/spherical-contact-lenses';
      const categoryLabel = kind === 'coloured' ? 'Browse coloured contact lenses' : 'Browse clear spherical lenses';
      if (!matches.length) return `<div class="clc-pathway"><h3>No exact or close ${kind} product matches found</h3><p>No stored orderable Sphere value is within ${MAX_SPHERE_DIFFERENCE_D.toFixed(2)} D of this estimate. Ask our optometry team to confirm the available pathway.</p><div class="clc-next-step__actions"><a class="button button--secondary" href="${categoryHref}" data-clc-route="${kind}">${categoryLabel}</a><a class="clc-next-step__support" href="/pages/contact#ContactForm" data-clc-route="support">Ask our optometry team</a></div></div>`;
      const cards = matches.map(({ product, eyeMatches }) => {
        const eyeRows = eyeMatches.map(({ label, target, options }) => `<li><strong>${label}</strong><span class="clc-toric-calculated"><small>Estimated spherical equivalent</small>${api.formatPower(target)}</span><div class="clc-toric-options">${options.map((option, index) => `<div class="clc-toric-option"><small>${options.length > 1 ? `Equal option ${index + 1}` : 'Closest option'}</small><strong>${api.formatPower(option.power)}</strong><span>Difference: ${option.difference.toFixed(2)} D</span></div>`).join('')}</div></li>`).join('');
        return `<a href="${escapeHtml(safeStorePath(product.url))}" class="clc-product clc-product--toric clc-product--spherical-closest" data-clc-route="${kind}_product"><img src="${escapeHtml(safeImage(product.image))}" alt="" loading="lazy" width="180" height="180"><span class="clc-product__body"><strong>${escapeHtml(product.title)}</strong><small class="clc-product__availability">Closest listed spherical power${eyeMatches.some((eye) => eye.options.length > 1) ? 's' : ''}</small><ul class="clc-toric-parameters">${eyeRows}</ul></span></a>`;
      }).join('');
      return `<section id="ClcProductMatchesList" class="clc-matches clc-matches--spherical-closest"><h3>Closest listed ${kind === 'coloured' ? 'coloured' : 'clear spherical'} options</h3><p>No product contains the exact estimated spherical power. These are the nearest stored orderable Sphere values within ${MAX_SPHERE_DIFFERENCE_D.toFixed(2)} D. The calculator does not choose between equal options; final contact lens power should be confirmed with an optometrist.</p><div class="clc-matches__grid">${cards}</div></section>`;
    };

    const formatToricParameters = (target, axis = target.axis) => `${api.formatPower(target.sphere, false)} / ${api.formatPower(target.cylinder, false)} × ${axis}`;
    const toricTargets = (eyeResults) => eyeResults
      .map((result) => ({ eye: result._eye || 'od', label: result._eye === 'os' ? 'Left eye / OS' : 'Right eye / OD', target: toricTargetForResult(mode, result) }))
      .filter((entry) => entry.target);
    const toricProductCards = (eyeResults, toricPrimary, eligibleToricProducts, selectedIntent) => {
      const targets = toricTargets(eyeResults);
      if (!toricPrimary) return {
        html: '<section class="clc-next-step clc-next-step--toric clc-next-step--secondary"><p class="clc-next-step__eyebrow">Alternative pathway</p><h3>Toric contact lenses</h3><p>A toric lens may still be worth discussing if vision is not clear through a spherical lens.</p><div class="clc-next-step__actions"><a class="button button--secondary" href="/collections/toric-astigmatism-contact-lenses" data-clc-route="toric">View toric contact lenses</a><a class="clc-next-step__support" href="/pages/contact#ContactForm" data-clc-route="support">Ask our optometry team</a></div></section>',
        matchCount: 0,
        matchState: 'not_applicable',
        exactHandles: []
      };
      const matches = toricPrimary ? matchToricProducts(eligibleToricProducts, targets) : [];
      const cards = matches.map(({ product, eyeMatches }) => {
        const exactForAllEyes = eyeMatches.every(({ match }) => match.state === 'exact');
        const eyeRows = eyeMatches.map(({ label, target, match }) => {
          const options = match.options.map((option, index) => {
            const differences = [
              option.sphereDifference > 0.001 ? `Sphere ${option.sphereDifference.toFixed(2)} D` : '',
              option.cylinderDifference > 0.001 ? `Cylinder ${option.cylinderDifference.toFixed(2)} D` : '',
              option.axisDifference > 0.001 ? `Axis ${option.axisDifference}°` : ''
            ].filter(Boolean).join(' · ');
            return `<div class="clc-toric-option"><small>${match.state === 'exact' ? 'Available' : `Option ${index + 1}`}</small><strong>${formatToricParameters(option)}</strong>${differences ? `<span>Difference: ${differences}</span>` : '<span>Exact Sphere, Cylinder and Axis</span>'}</div>`;
          }).join('');
          const targetLabel = mode === MODE.CONTACT_LENS ? 'Entered' : 'Calculated';
          return `<li><strong>${label}</strong><span class="clc-toric-calculated"><small>${targetLabel}</small>${formatToricParameters(target)}</span><div class="clc-toric-options">${options}</div></li>`;
        }).join('');
        return `<a href="${escapeHtml(safeStorePath(product.url))}" class="clc-product clc-product--toric" data-clc-route="toric_product"><img src="${escapeHtml(safeImage(product.image))}" alt="" loading="lazy" width="180" height="180"><span class="clc-product__body"><strong>${escapeHtml(product.title)}</strong>${product.pack ? `<small class="clc-product__pack">${escapeHtml(product.pack)}</small>` : ''}<small class="clc-product__availability ${exactForAllEyes ? 'clc-product__availability--exact' : ''}">${exactForAllEyes ? 'Exact listed parameters' : 'Closest listed parameters'}</small><ul class="clc-toric-parameters">${eyeRows}</ul></span></a>`;
      }).join('');
      const hasExact = matches.some(({ eyeMatches }) => eyeMatches.every(({ match }) => match.state === 'exact'));
      const hasClosest = matches.some(({ eyeMatches }) => eyeMatches.some(({ match }) => match.state === 'closest'));
      const heading = hasExact && hasClosest ? 'Exact and closest listed toric options' : hasExact ? 'Exact listed toric parameters' : 'Closest listed toric options';
      const explanation = hasClosest
        ? (mode === MODE.CONTACT_LENS
          ? 'Your entered contact lens parameters fall between available listed powers for one or more lenses. These are the closest orderable parameter options. Final contact lens power should be confirmed after assessing lens fit, rotation and vision.'
          : 'Your calculated starting power falls between available listed powers for one or more lenses. These are the closest orderable parameter options. Final contact lens power should be confirmed after assessing lens fit, rotation and vision.')
        : (mode === MODE.CONTACT_LENS
          ? 'These products contain the exact entered Sphere, Cylinder and Axis in their structured ordering parameters. Final contact lens power should still be confirmed after assessing lens fit, rotation and vision.'
          : 'These products contain the exact calculated Sphere, Cylinder and Axis in their structured ordering parameters. Final contact lens power should still be confirmed after assessing lens fit, rotation and vision.');
      const body = matches.length
        ? `<div class="clc-matches clc-matches--toric"><h3>${heading}</h3><p>${explanation}</p><div class="clc-matches__grid">${cards}</div></div>`
        : `<div class="clc-pathway clc-pathway--toric-empty"><h3>No close listed toric parameter option found.</h3><p>${targets.length ? `No structured orderable combination is within ${MAX_SPHERE_DIFFERENCE_D.toFixed(2)} D Sphere, ${MAX_CYLINDER_DIFFERENCE_D.toFixed(2)} D Cylinder and ${MAX_AXIS_DIFFERENCE_DEG}° Axis for every entered eye.` : 'The current result does not contain a matchable toric parameter set.'}</p></div>`;
      const browseHref = selectedIntent === 'colour' ? '/collections/colour-contacts' : '/collections/toric-astigmatism-contact-lenses';
      const browseLabel = selectedIntent === 'colour' ? 'Browse coloured contact lenses' : 'View toric contact lenses';
      return {
        html: `<section class="clc-next-step clc-next-step--toric"><p class="clc-next-step__eyebrow">${toricPrimary ? 'Toric pathway' : 'Alternative pathway'}</p>${body}<div class="clc-next-step__actions"><a class="button ${toricPrimary ? 'button--primary' : 'button--secondary'}" href="${browseHref}" data-clc-route="${selectedIntent === 'colour' ? 'colour' : 'toric'}">${browseLabel}</a><a class="clc-next-step__support" href="/pages/contact#ContactForm" data-clc-route="support">Ask our optometry team</a></div></section>`,
        matchCount: matches.length,
        matchState: hasExact ? (hasClosest ? 'exact_and_closest' : 'exact') : hasClosest ? 'closest' : 'none',
        exactHandles: matches.filter(({ eyeMatches }) => eyeMatches.every(({ match }) => match.state === 'exact')).map(({ product }) => product.handle).filter(Boolean)
      };
    };

    const compactSphericalAlternative = () => '<section class="clc-discovery clc-discovery--secondary clc-discovery--compact"><h3>Want to explore spherical lenses anyway?</h3><a class="button button--secondary clc-discovery__browse" href="/collections/spherical-contact-lenses" data-clc-route="spherical">View spherical alternatives</a></section>';
    const colouredToricUnavailablePanel = (band) => `<section class="clc-next-step clc-next-step--toric clc-next-step--colour-unavailable" data-clc-colour-toric-unavailable><p class="clc-next-step__eyebrow">Coloured lens pathway</p><h3>No toric coloured contact lenses available</h3><p>${colouredAstigmatismShoppingCopy(band)}</p><div class="clc-next-step__actions"><button class="button button--primary" type="button" data-clc-switch-clear>View clear toric contact lenses</button><a class="button button--secondary" href="/collections/colour-contacts" data-clc-route="colour">Browse coloured lenses anyway</a></div></section>`;

    const currentClSphericalSelector = (selected = '') => {
      const option = (value, label) => `<button class="button ${selected === value ? 'button--primary' : 'button--secondary'}" type="button" data-clc-spherical-type="${value}" aria-pressed="${selected === value}">${label}</button>`;
      return `<fieldset class="clc-intent clc-intent--secondary" data-clc-spherical-intent><legend>What type of spherical lens are you looking for?</legend><p>Choose a product type to see exact or closest listed Sphere options.</p><div class="clc-intent__options clc-intent__options--two">${option('clear', 'Clear spherical lenses')}${option('colour', 'Coloured spherical lenses')}</div></fieldset>`;
    };
    const currentClHighColouredGoalPanel = () => '<section class="clc-next-step clc-next-step--toric" data-clc-high-coloured-goal><p class="clc-next-step__eyebrow">Coloured spherical lens pathway</p><h3>Keep your astigmatism correction</h3><p>Your astigmatism is high enough that a spherical coloured lens is generally not recommended because too much cylinder correction would be omitted.</p><div class="clc-next-step__actions"><button class="button button--primary" type="button" data-clc-choose-toric>View clear toric contact lenses</button><a class="clc-next-step__support" href="/pages/contact#ContactForm" data-clc-route="support">Ask our optometry team</a></div></section>';

    const renderCurrentClRouting = (eyeResults, region, band) => {
      if (!intent) {
        region.innerHTML = '<p class="clc-intent__prompt">Choose how you would like to use this result.</p>';
        return;
      }
      if (intent === 'checking') {
        region.innerHTML = '<section class="clc-next-step"><p class="clc-next-step__eyebrow">Your power comparison is ready</p><p>No products are shown in this view.</p><div class="clc-next-step__actions"><a class="button button--primary" href="/pages/contact#ContactForm" data-clc-route="support">Ask our optometry team</a></div></section>';
        return;
      }
      if (intent === 'goal_high') {
        region.innerHTML = currentClHighColouredGoalPanel();
        return;
      }
      if (intent === 'keep_toric') {
        const clearToricProducts = selectToricIntentCatalogue('clear', toricProducts);
        const toricResult = toricProductCards(eyeResults, true, clearToricProducts, 'clear');
        const returnAction = returnTarget?.productCategory === 'clear'
          && returnTarget.productType === 'toric'
          && toricResult.exactHandles.includes(returnTarget.handle)
          ? `<section class="clc-next-step clc-next-step--return"><p class="clc-next-step__eyebrow">Exact listed parameters available</p><div class="clc-next-step__actions"><a class="button button--primary" href="${escapeHtml(safeStorePath(returnTarget.href))}" data-clc-route="return">${escapeHtml(returnTarget.label ? `Return to ${returnTarget.label}` : 'Return to product')}</a></div></section>`
          : '';
        region.innerHTML = `${toricResult.html}${returnAction}`;
        return;
      }
      if (intent !== 'use_spherical') return;
      if (band === 'high') {
        region.innerHTML = currentClHighColouredGoalPanel();
        return;
      }
      if (!sphericalIntent) {
        region.innerHTML = currentClSphericalSelector();
        return;
      }
      const catalogue = selectIntentCatalogue(sphericalIntent, { clear: clearProducts, colour: colourProducts });
      const allExactMatches = matchingProducts(catalogue, eyeResults, 0);
      const allClosestMatches = allExactMatches.length ? [] : closestSphericalProductMatches(catalogue, eyeResults);
      const kind = sphericalIntent === 'colour' ? 'coloured' : 'clear';
      const matchingReturnProduct = returnTarget
        && returnTarget.productCategory === sphericalIntent
        && allExactMatches.find((product) => product.handle === params.get('return_product'));
      const exactMatches = prioritiseReturnProduct(allExactMatches, matchingReturnProduct?.handle, 4);
      const closestMatches = allClosestMatches.slice(0, 4);
      const returnAction = matchingReturnProduct
        ? `<section class="clc-next-step clc-next-step--return"><p class="clc-next-step__eyebrow">Available in this spherical power</p>${returnTarget.shadeLabel ? `<p class="clc-next-step__shade">${escapeHtml(returnTarget.shadeLabel)}</p>` : ''}<div class="clc-next-step__actions"><a class="button button--primary" href="${escapeHtml(safeStorePath(returnTarget.href))}" data-clc-route="return">${escapeHtml(returnTarget.label ? `Return to ${returnTarget.label}` : 'Return to product')}</a></div></section>`
        : '';
      const clarityLead = band === 'moderate' && sphericalIntent === 'colour' ? '<h3>Toric correction is likely to provide clearer vision.</h3>' : '';
      const sphericalCards = exactMatches.length ? productCards(exactMatches, kind, 'spherical power') : closestSphericalProductCards(closestMatches, kind);
      const sphericalTypeSwitcher = currentClGoal === CURRENT_CL_GOAL.COLOURED_SPHERICAL ? '' : currentClSphericalSelector(sphericalIntent);
      region.innerHTML = `${sphericalTypeSwitcher}${returnAction}<section class="clc-discovery clc-discovery--current-cl"><p class="clc-next-step__eyebrow">Spherical pathway</p>${clarityLead}<p class="clc-discovery__warning">${currentClSphericalCopy(band, sphericalIntent === 'colour')}</p>${sphericalCards}</section>`;
    };

    const renderRouting = (eyeResults) => {
      const region = root.querySelector('[data-clc-product-matches]');
      const sidebarShoppingLink = root.querySelector('[data-clc-sidebar-shopping-link]');
      const band = highestBand(eyeResults);
      if (mode === MODE.CONTACT_LENS) {
        if (sidebarShoppingLink) sidebarShoppingLink.hidden = intent === 'checking';
        renderCurrentClRouting(eyeResults, region, band);
        return;
      }
      if (sidebarShoppingLink) sidebarShoppingLink.hidden = intent === 'checking';
      if (!intent) { region.innerHTML = '<p class="clc-intent__prompt">Choose an option above to see relevant products or support.</p>'; return; }
      if (intent === 'checking') {
        region.innerHTML = '<section class="clc-next-step"><p class="clc-next-step__eyebrow">Your estimate is ready</p><p>No products are shown in this view. Keep the result for discussion with your optometrist, or ask our team for help.</p><div class="clc-next-step__actions"><a class="button button--primary" href="/pages/contact#ContactForm" data-clc-route="support">Ask our optometry team</a></div></section>';
        return;
      }
      const toricPrimary = !allowIndividualSphericalCards(band);
      const catalogue = selectIntentCatalogue(intent, { clear: clearProducts, colour: colourProducts });
      const exactMatches = matchingProducts(catalogue, eyeResults);
      const kind = intent === 'colour' ? 'coloured' : 'clear';
      const categoryHref = intent === 'colour' ? '/collections/colour-contacts' : '/collections/spherical-contact-lenses';
      const eligibleToricProducts = selectToricIntentCatalogue(intent, toricProducts);
      const toricResult = toricPrimary ? toricProductCards(eyeResults, true, eligibleToricProducts, intent) : { html: '', matchCount: 0, matchState: 'not_applicable' };
      const recommendation = intentRecommendationModel(intent, band, eligibleToricProducts.length, toricResult.matchCount, exactMatches.length);
      const intentCategory = intent === 'colour' ? 'colour' : 'clear';
      const returnIsExactSpherical = !toricPrimary && returnTarget?.productType === 'spherical' && exactMatches.some((product) => product.handle === returnTarget.handle);
      const returnIsExactToric = toricPrimary && returnTarget?.productType === 'toric' && toricResult.exactHandles?.includes(returnTarget.handle);
      const returnAction = returnTarget && returnTarget.productCategory === intentCategory && (returnIsExactSpherical || returnIsExactToric)
        ? `<section class="clc-next-step clc-next-step--return"><p class="clc-next-step__eyebrow">Exact product match</p>${returnTarget.shadeLabel ? `<p class="clc-next-step__shade">${escapeHtml(returnTarget.shadeLabel)}</p>` : ''}<div class="clc-next-step__actions"><a class="button button--primary" href="${escapeHtml(safeStorePath(returnTarget.href))}" data-clc-route="return">${escapeHtml(returnTarget.label ? `Return to ${returnTarget.label}` : 'Return to product')}</a></div></section>`
        : '';
      const sphericalProducts = `<section class="clc-discovery"><p class="clc-next-step__eyebrow">Spherical pathway</p>${productCards(exactMatches, kind)}<a class="button button--secondary clc-discovery__browse" href="${categoryHref}" data-clc-route="${kind}">Browse all ${kind} contact lenses</a></section>`;
      if (recommendation.pathway === 'clear_toric') {
        region.innerHTML = `${toricResult.html}${returnAction}${compactSphericalAlternative()}`;
      } else if (recommendation.pathway === 'colour_toric') {
        region.innerHTML = `${toricResult.html}${returnAction}`;
      } else if (recommendation.pathway === 'colour_toric_guidance') {
        region.innerHTML = `${returnAction}${colouredToricUnavailablePanel(band)}`;
      } else {
        region.innerHTML = `${returnAction}${sphericalProducts}`;
      }
    };

    modeInputs.forEach((input) => input.addEventListener('change', () => {
      if (!input.checked || ![MODE.GLASSES, MODE.CONTACT_LENS].includes(input.value)) return;
      mode = input.value;
      latestEyeResults = [];
      sphericalIntent = '';
      intent = mode === MODE.CONTACT_LENS ? '' : defaultIntent(context, returnTarget);
      applyMode();
      results.hidden = true;
      errors.hidden = true;
      errors.innerHTML = '';
      const url = new URL(window.location.href);
      url.searchParams.set('mode', mode);
      window.history.replaceState(window.history.state, '', url.toString());
    }));

    intentInputs.forEach((input) => {
      input.checked = input.value === intent;
      input.addEventListener('change', () => {
        if (!input.checked) return;
        intent = input.value;
        if (mode === MODE.CONTACT_LENS) {
          sphericalIntent = intent === 'use_spherical' && currentClGoal === CURRENT_CL_GOAL.COLOURED_SPHERICAL ? 'colour' : '';
        }
        renderRouting(latestEyeResults);
        if (mode === MODE.CONTACT_LENS) {
          pushEvent('pathway_choice', { ...analyticsDetails(), pathway_choice: intent === 'keep_toric' ? 'keep_toric' : intent === 'use_spherical' ? 'use_spherical' : 'checking_only' });
        } else {
          pushEvent('pathway_choice', { ...analyticsDetails(), pathway_choice: intent === 'clear' ? 'view_clear_spherical' : intent === 'colour' ? 'view_coloured_spherical' : 'checking_only' });
        }
      });
    });
    root.addEventListener('click', (event) => {
      const sphericalButton = event.target.closest?.('[data-clc-spherical-type]');
      if (sphericalButton && mode === MODE.CONTACT_LENS) {
        event.preventDefault();
        const scrollPosition = window.scrollY;
        sphericalIntent = sphericalButton.dataset.clcSphericalType === 'colour' ? 'colour' : 'clear';
        renderRouting(latestEyeResults);
        pushEvent('pathway_choice', { ...analyticsDetails(), pathway_choice: sphericalIntent === 'colour' ? 'view_coloured_spherical' : 'view_clear_spherical' });
        if (window.matchMedia?.('(max-width: 749px)').matches) {
          root.querySelector('[data-clc-product-matches] .clc-discovery--current-cl')?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        } else {
          const restoreScrollPosition = () => window.scrollTo({ top: scrollPosition, behavior: 'auto' });
          window.requestAnimationFrame(() => {
            restoreScrollPosition();
            window.requestAnimationFrame(restoreScrollPosition);
          });
          window.setTimeout(restoreScrollPosition, 150);
        }
        return;
      }
      const chooseToricButton = event.target.closest?.('[data-clc-choose-toric]');
      if (chooseToricButton && mode === MODE.CONTACT_LENS) {
        event.preventDefault();
        intent = 'keep_toric';
        sphericalIntent = '';
        configureIntentControl(highestBand(latestEyeResults));
        renderRouting(latestEyeResults);
        pushEvent('pathway_choice', { ...analyticsDetails(), pathway_choice: 'keep_toric' });
        root.querySelector('[data-clc-product-matches] .clc-next-step--toric')?.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        return;
      }
      const switchButton = event.target.closest?.('[data-clc-switch-clear]');
      if (!switchButton) return;
      event.preventDefault();
      const clearInput = intentInputs.find((input) => input.value === 'clear');
      if (!clearInput) return;
      intent = 'clear';
      clearInput.checked = true;
      renderRouting(latestEyeResults);
      pushEvent('pathway_choice', { ...analyticsDetails(), pathway_choice: 'view_clear_spherical' });
      const clearResults = root.querySelector('[data-clc-product-matches] .clc-next-step--toric');
      if (clearResults) {
        clearResults.tabIndex = -1;
        clearResults.focus({ preventScroll: true });
        clearResults.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const inputMessages = powerComboboxes.map((combobox) => {
        const parsed = combobox.validate();
        if (parsed.valid) return '';
        const eye = combobox.input.closest('.clc-eye')?.querySelector('legend')?.textContent?.trim() || 'Eye';
        return `${eye}: ${parsed.error}`;
      }).filter(Boolean);
      if (inputMessages.length) {
        errors.hidden = false;
        errors.innerHTML = `<strong>Please check your prescription.</strong><ul>${inputMessages.map((message) => `<li>${escapeHtml(message)}</li>`).join('')}</ul>`;
        results.hidden = true;
        errors.focus();
        return;
      }
      const calculate = mode === MODE.CONTACT_LENS ? api.calculateContactLensEye : api.calculateEye;
      const od = calculate(readEye('od'));
      const os = calculate(readEye('os'));
      const eyeResults = [od, os];
      const messages = [];
      if (od.empty && os.empty) messages.push('Enter a prescription for at least one eye.');
      [['Right eye', od], ['Left eye', os]].forEach(([label, result]) => result.errors?.forEach((message) => messages.push(`${label}: ${message}`)));
      errors.hidden = !messages.length;
      errors.innerHTML = messages.length ? `<strong>Please check your prescription.</strong><ul>${messages.map((message) => `<li>${message}</li>`).join('')}</ul>` : '';
      if (messages.length) { results.hidden = true; errors.focus(); return; }
      const valid = [{ result: od, eye: 'od' }, { result: os, eye: 'os' }]
        .filter(({ result }) => result.valid)
        .map(({ result, eye }) => ({ ...result, _eye: eye }));
      root.querySelector('[data-clc-result-cards]').innerHTML = valid.map((result) => createResult(result._eye, result)).join('');
      latestEyeResults = valid;
      const band = highestBand(valid);
      if (mode === MODE.CONTACT_LENS) {
        const decision = currentClDecisionModel(band, currentClGoal);
        intent = decision.autoPathway;
        sphericalIntent = decision.autoSphericalIntent;
        intentControl.hidden = decision.autoPathway === 'goal_high';
      } else intentControl.hidden = false;
      configureIntentControl(band);
      intentInputs.forEach((input) => { input.checked = input.value === intent; });
      renderRouting(valid);
      results.hidden = false;
      results.focus();
      pushEvent('converter_result_viewed', analyticsDetails());
    });

    form.addEventListener('reset', () => {
      window.setTimeout(() => {
        powerComboboxes.forEach((combobox) => combobox.reset());
        errors.hidden = true;
        errors.innerHTML = '';
        results.hidden = true;
        intentControl.hidden = true;
        latestEyeResults = [];
        sphericalIntent = '';
        intent = mode === MODE.CONTACT_LENS ? '' : defaultIntent(context, returnTarget);
      }, 0);
    });
    root.addEventListener('click', (event) => {
      const routeLink = event.target.closest('[data-clc-route]');
      if (!routeLink) return;
      const route = routeLink.dataset.clcRoute || '';
      if (route === 'return' || route === 'back') {
        pushEvent('return_to_pdp', { ...analyticsDetails(), return_action: route === 'return' ? 'eligible_result' : 'neutral_back' });
        return;
      }
      const productPathway = /toric/.test(route) ? 'toric' : /colou?r/.test(route) ? 'colour' : /clear|spherical/.test(route) ? 'clear' : '';
      if (!productPathway) return;
      const matchType = routeLink.querySelector?.('.clc-product__availability--exact')
        ? 'exact'
        : /closest/i.test(routeLink.textContent || '')
          ? 'closest'
          : 'category_only';
      pushEvent('view_product_options', { ...analyticsDetails(), product_pathway: productPathway, match_type: matchType });
    });
  };

  window.CityOpticsContactLensInput = Object.freeze({ POWER_INPUT, SPHERE_SUGGESTION_RANGE, cleanPowerText, formatPowerInput, parsePowerInput, powerOptions, powerSuggestions, axisInputValid });
  window.CityOpticsContactLensRouting = Object.freeze({ CONTEXT, MODE, CURRENT_CL_GOAL, MAX_SPHERE_DIFFERENCE_D, MAX_CYLINDER_DIFFERENCE_D, MAX_AXIS_DIFFERENCE_DEG, MAX_NEAREST_AXIS_DISTANCE_DEG, normaliseProductType, resolveContext, routeModel, safeReturn, resultPriorityModel, allowIndividualSphericalCards, defaultIntent, currentClDecisionModel, currentClSphericalCopy, cylinderWarning, selectIntentCatalogue, productMatchesIntent, selectToricIntentCatalogue, colouredAstigmatismShoppingCopy, intentRecommendationModel, exactMatchingProducts, closestListedSphereOptions, closestSphericalProductMatches, prioritiseReturnProduct, discoveryOrderModel, numericValues, normaliseAxis, axisDistance, ruleCylinderAxes, toricTargetForResult, compareToricCandidates, listedToricCandidates, matchToricProduct, toricProductScore, matchToricProducts, hasExactToricPowerWithoutCloseAxis });

  const mount = () => document.querySelectorAll('[data-clc-root]').forEach(init);
  document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', mount) : mount();
})();
